import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Avatar,
  AvatarGroup,
  CircularProgress,
  Fab,
  Skeleton,
  Fade,
  Zoom,
  Grow,
  Collapse,
  alpha,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  ContentCopy as DuplicateIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  AccountTree as ProcessFlowIcon,
  Star as StarIcon,
  CloudUpload as ImportIcon,
  Public as PublicIcon,
  Assignment as PlanningIcon,
  Settings as ProcessIcon,
  Sync as WorkflowIcon,
  Description as DocumentationIcon,
  DashboardCustomize as TemplateIcon,
  FolderOpen as OtherIcon,
  Layers as LayersIcon,
  Timeline as TimelineIcon,
  Group as GroupIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useProcessFlowStore from '../../stores/processFlowStore';
import MagicCard from '../../components/ui/MagicCard';
import { useSnackbar } from 'notistack';
import { useTheme } from '@mui/material/styles';

function ProcessFlows() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredCard, setHoveredCard] = useState(null);

  const {
    processFlows,
    isLoading,
    filters,
    pagination,
    fetchProcessFlows,
    setFilters,
    setPagination,
    deleteProcessFlow,
    duplicateProcessFlow
  } = useProcessFlowStore();

  useEffect(() => {
    fetchProcessFlows();
  }, [filters, pagination.page]);

  const handleCreateNew = () => {
    navigate('/process-flows/create');
  };

  const handleView = (flowId) => {
    navigate(`/process-flows/${flowId}`);
  };

  const handleEdit = (flowId) => {
    navigate(`/process-flows/${flowId}/edit`);
  };

  const handleMenuOpen = (event, flow) => {
    setAnchorEl(event.currentTarget);
    setSelectedFlow(flow);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFlow(null);
  };

  const handleDelete = async () => {
    if (selectedFlow) {
      try {
        await deleteProcessFlow(selectedFlow._id);
        enqueueSnackbar('Process flow deleted successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Failed to delete process flow', { variant: 'error' });
      }
    }
    handleMenuClose();
  };

  const handleDuplicate = async () => {
    if (selectedFlow) {
      try {
        const duplicated = await duplicateProcessFlow(selectedFlow._id, {
          name: `${selectedFlow.name} (Copy)`
        });
        enqueueSnackbar('Process flow duplicated successfully', { variant: 'success' });
        navigate(`/process-flows/${duplicated._id}/edit`);
      } catch (error) {
        enqueueSnackbar('Failed to duplicate process flow', { variant: 'error' });
      }
    }
    handleMenuClose();
  };

  const handleShare = () => {
    if (selectedFlow) {
      navigate(`/process-flows/${selectedFlow._id}/share`);
    }
    handleMenuClose();
  };

  const handleExport = async () => {
    if (selectedFlow) {
      try {
        await useProcessFlowStore.getState().exportProcessFlow(selectedFlow._id, 'json');
        enqueueSnackbar('Process flow exported successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Failed to export process flow', { variant: 'error' });
      }
    }
    handleMenuClose();
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setFilters({ search: event.target.value });
  };

  const handleCategoryFilter = (event) => {
    setFilters({ category: event.target.value });
  };

  const handleStatusFilter = (event) => {
    setFilters({ status: event.target.value });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      draft: 'warning',
      review: 'info',
      approved: 'primary',
      published: 'success',
      archived: 'error'
    };
    return statusColors[status] || 'default';
  };

  const getCategoryIcon = (category) => {
    const categoryIcons = {
      planning: PlanningIcon,
      process: ProcessIcon,
      workflow: WorkflowIcon,
      documentation: DocumentationIcon,
      template: TemplateIcon,
      other: OtherIcon
    };
    const Icon = categoryIcons[category] || OtherIcon;
    return <Icon fontSize="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header matching dashboard theme */}
      <Paper sx={{
        p: 3,
        mb: 3,
        background: theme.palette.background.paper,
        borderRadius: theme.aghrba?.borderRadius?.card || 2
      }}>
        <Grid container alignItems="center" justifyContent="space-between" spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                p: 1.5,
                borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                mr: 2
              }}>
                <ProcessFlowIcon sx={{
                  color: theme.palette.primary.main,
                  fontSize: 28
                }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="600" color="text.primary">
                  Process Flows
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Design and manage your business workflows
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                px: 3,
                background: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
                '&:hover': {
                  background: theme.palette.secondary.dark,
                }
              }}
            >
              Create New Flow
            </Button>
          </Grid>
        </Grid>

        {/* Filters */}
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search flows..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category || ''}
                  onChange={handleCategoryFilter}
                  label="Category"
                  sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5) }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="planning">Planning</MenuItem>
                  <MenuItem value="process">Process</MenuItem>
                  <MenuItem value="workflow">Workflow</MenuItem>
                  <MenuItem value="documentation">Documentation</MenuItem>
                  <MenuItem value="template">Template</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  onChange={handleStatusFilter}
                  label="Status"
                  sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5) }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="review">Review</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ImportIcon />}
                size="small"
                sx={{
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  textTransform: 'none'
                }}
              >
                Import
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Process Flows Grid */}
      {isLoading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item}>
              <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : processFlows.length === 0 ? (
        <Paper sx={{
          p: 6,
          textAlign: 'center',
          background: theme.palette.background.paper,
          borderRadius: 2
        }}>
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: alpha(theme.palette.primary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            mb: 3
          }}>
            <ProcessFlowIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
          </Box>
          <Typography variant="h6" gutterBottom>
            No Process Flows Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Start creating visual workflows to document your processes
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            sx={{
              textTransform: 'none',
              background: theme.palette.secondary.main,
              color: theme.palette.secondary.contrastText,
              '&:hover': {
                background: theme.palette.secondary.dark,
              }
            }}
          >
            Create Your First Flow
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {processFlows.map((flow) => (
            <Grid item xs={12} md={6} lg={4} key={flow._id}>
              <MagicCard
                magical
                level={flow.status === 'published' ? 2 : 1}
                cardColor={flow.status === 'published' ? 'secondary' : 'primary'}
              >
                <CardContent>
                  {/* Card Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{
                          p: 0.75,
                          borderRadius: 1,
                          background: alpha(theme.palette.primary.main, 0.1),
                          display: 'flex',
                          mr: 1.5
                        }}>
                          {getCategoryIcon(flow.category)}
                        </Box>
                        <Typography variant="h6" sx={{
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {flow.name}
                        </Typography>
                      </Box>

                      {/* Status Chips */}
                      <Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }}>
                        <Chip
                          label={flow.status}
                          size="small"
                          color={getStatusColor(flow.status)}
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                        {flow.isTemplate && (
                          <Chip
                            icon={<StarIcon sx={{ fontSize: 14 }} />}
                            label="Template"
                            size="small"
                            color="secondary"
                            sx={{ height: 22, fontSize: '0.75rem' }}
                          />
                        )}
                        {flow.isPublic && (
                          <PublicIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                        )}
                      </Stack>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, flow)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      minHeight: 40,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {flow.description || 'No description available'}
                  </Typography>

                  {/* Stats */}
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 1,
                    background: alpha(theme.palette.background.default, 0.3),
                    mb: 2
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <LayersIcon sx={{ fontSize: 18, color: theme.palette.primary.main, mb: 0.5 }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                            {flow.nodes?.length || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Nodes
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <TimelineIcon sx={{ fontSize: 18, color: theme.palette.secondary.main, mb: 0.5 }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                            {flow.edges?.length || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Edges
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <GroupIcon sx={{ fontSize: 18, color: theme.palette.info.main, mb: 0.5 }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                            {flow.shares?.length || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Shares
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Footer */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(flow.updatedAt).toLocaleDateString()}
                      </Typography>
                    </Box>

                    {flow.shares && flow.shares.length > 0 && (
                      <AvatarGroup max={3} sx={{
                        '& .MuiAvatar-root': {
                          width: 24,
                          height: 24,
                          fontSize: 10,
                          bgcolor: theme.palette.primary.main
                        }
                      }}>
                        {flow.shares.map((share) => (
                          <Avatar key={share.userId?._id || share.userId}>
                            {share.userId?.firstName?.[0] || '?'}
                          </Avatar>
                        ))}
                      </AvatarGroup>
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => handleView(flow._id)}
                    sx={{ textTransform: 'none' }}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEdit(flow._id)}
                    sx={{ textTransform: 'none' }}
                  >
                    Edit
                  </Button>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, flow)}
                  >
                    <ShareIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </MagicCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleView(selectedFlow?._id);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          handleEdit(selectedFlow?._id);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon>
            <DuplicateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleShare}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExport}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Floating Action Button for Mobile */}
      <Zoom in>
        <Fab
          color="secondary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' }
          }}
          onClick={handleCreateNew}
        >
          <AddIcon />
        </Fab>
      </Zoom>
    </Box>
  );
}

export default ProcessFlows;